/**
 * Google Analytics Gateway
 *
 * Detects and sends events to Google Analytics (GA4 or GTM).
 * Ported from web/src/adapters/gateways/GoogleAnalyticsGateway.js
 */

import type { GoogleAnalyticsGateway as IGoogleAnalyticsGateway } from '@/domain/gateway';
import type { WidgetEventName } from '@/domain/widgetEvent';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Record<string, unknown>[];
  }
}

type AnalyticsLibrary =
  | { type: 'GA4'; instance: (...args: unknown[]) => void }
  | { type: 'GTM'; instance: Record<string, unknown>[] }
  | { type: 'NONE' };

export default class GoogleAnalyticsGateway implements IGoogleAnalyticsGateway {
  private customTrackingId: string | null;

  constructor() {
    this.customTrackingId = null;
  }

  setCustomTrackingId(customTrackingId: string): void {
    this.customTrackingId = customTrackingId;
  }

  sendEvent(eventName: WidgetEventName, parameters: Record<string, unknown> = {}): void {
    const library = this.detectAnalyticsLibrary();

    if (library.type === 'NONE') {
      return;
    }

    try {
      const normalizedEventName = this.normalizeEventName(eventName);
      const eventData = { event_category: 'gladly_chat_widget', ...parameters };

      if (library.type === 'GA4') {
        this.sendGA4Event(library.instance, normalizedEventName, eventData);
        return;
      }

      if (library.type === 'GTM') {
        this.sendGTMEvent(library.instance, normalizedEventName, eventData);
        return;
      }
    } catch {
      // Analytics errors should not interrupt application logic
    }
  }

  private detectAnalyticsLibrary(): AnalyticsLibrary {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      return { type: 'GA4', instance: window.gtag };
    }

    if (typeof window !== 'undefined' && window.dataLayer && Array.isArray(window.dataLayer)) {
      return { type: 'GTM', instance: window.dataLayer };
    }

    return { type: 'NONE' };
  }

  private normalizeEventName(eventName: string): string {
    const prefixedName = eventName.startsWith('gladly_') ? eventName : `gladly_${eventName}`;
    return prefixedName.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
  }

  private sendGA4Event(
    gtag: (...args: unknown[]) => void,
    eventName: string,
    parameters: Record<string, unknown>,
  ): void {
    const eventConfig = this.customTrackingId ? { send_to: this.customTrackingId, ...parameters } : parameters;
    gtag('event', eventName, eventConfig);
  }

  private sendGTMEvent(
    dataLayer: Record<string, unknown>[],
    eventName: string,
    parameters: Record<string, unknown>,
  ): void {
    dataLayer.push({
      event: eventName,
      ...parameters,
    });
  }
}
